import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiCreateDocumentGenerationJob, ApiService, CompanyService, FileModel } from '../../../../api';
import { RetrieveDocumentGenerationJob } from '../../../../api/models/RetrieveDocumentGenerationJob';
import { DOCUMENTS_BY_ID_QUERY_KEY } from '../../../../common/constants/services/documents';
import { EventType, useEventSubscription } from '../../../../context';
import { useAsyncProcessor } from '../../../../context/AsyncProcessorContext/AsyncProcessorContext';
import { useLayoutContext } from '../../../../context/LayoutContext';
import { useCitationContext } from '../../../../pages/CapTable/cap-table/components/captable-table/CitationContext';
import { PDFGenerationJobParsingMode } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { GenerationJobState } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { useDocuments } from '../../../../services/hooks';
import { Logger } from '../../../../utilities';
import PDFDocumentSearch from '../../../FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch';
import { SearchMatch } from '../../../FileViewer/pdf-viewer/pdf-document-search/PDFDocumentSearch.props';
import { PageData, PDFCitationPayload } from '../../../FileViewer/pdf-viewer/pdf-utils/constants';
import PDFViewer from '../../../FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode, SearchHighlight } from '../../../FileViewer/pdf-viewer/PDFViewer.props';
import { useAttributeMappedWidgetDrawer } from '../../attribute-mapped-widget-drawer/AttributeMappedWidgetDrawerContext';
import { useDrawerContext } from '../../draggable-drawer/DraggableDrawer.context';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import { PDFJobRibbon } from './pdf-job-ribbon/PDFJobRibbon';
import PDFPageSelectionControls from './pdf-page-selection-controls/PDFPageSelectionControls';
import { PDFViewerTabProps } from './PDFViewerTab.props';
import { acceptAllConfirmButtonSx } from './PDFViewerTab.styles';
import { FileViewWithExternalJobId } from './PDFViewerTab.types';

const PDFViewerTab = ({ document, companyId }: PDFViewerTabProps) => {
  const file = document.file as FileViewWithExternalJobId;
  const queryClient = useQueryClient();
  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const { currentMeasurementDate } = useLayoutContext();
  const { isPopped } = useDrawerContext();
  const { downloadDocumentContent } = useDocuments();
  const { addJob, removeJob, processingJobs } = useAsyncProcessor();
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [allowPageSelection, setAllowPageSelection] = useState(false);
  const [processingJob, setProcessingJob] = useState<RetrieveDocumentGenerationJob>();
  const [renderingJob, setRenderingJob] = useState<RetrieveDocumentGenerationJob>();
  const [genJobProcessing, setGenJobProcessing] = useState(false);
  const [renderJobProcessing, setRenderJobProcessing] = useState(false);
  const [finalizingResults, setFinalizingResults] = useState(false);
  const [jsonData, setJsonData] = useState<PageData[] | null>(null);

  const { activeCitation, setActiveCitation } = useCitationContext();
  const { onCitationPushed, onAcceptAllCitations, hasPendingCitationsForDocument } = useAttributeMappedWidgetDrawer();
  const [acceptAllDialogOpen, setAcceptAllDialogOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchHighlight, setSearchHighlight] = useState<SearchHighlight | null>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [controlsHeight, setControlsHeight] = useState(0);

  useLayoutEffect(() => {
    const el = controlsRef.current;
    if (!el) return;
    const measure = () => setControlsHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const parsingJobProcessorId = useMemo(
    () => document.file?.id ? `parsing-file-${document.file.id}` : null,
    [document.file?.id]
  );

  const hasGlobalParsingJob = useMemo(
    () => parsingJobProcessorId ? processingJobs.some(j => j.id === parsingJobProcessorId) : false,
    [processingJobs, parsingJobProcessorId]
  );

  // Clear active citation when the spreadsheet active cell changes
  useEventSubscription(EventType.SPREADSHEET_ACTIVE_CELL_CHANGED, () => {
    setActiveCitation();
  });

  // Clear active citation when this document changes
  useEffect(() => {
    setActiveCitation();
  }, [setActiveCitation, document.document_reference_id]);

  // ESC to dismiss active citation
  useEffect(() => {
    const targetWindow = controlsRef.current?.ownerDocument?.defaultView ?? window;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeCitation) {
        setActiveCitation();
      }
    };
    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [activeCitation, setActiveCitation, isPopped]);

  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });

  const parserResultsQueryKey = useMemo(
    () => ['document', document.document_reference_id, 'parser-results'],
    [document.document_reference_id]
  );

  const { data: parserResultsData } = useQuery({
    queryKey: parserResultsQueryKey,
    enabled: !!document.file?.json_file_path,
    staleTime: 0,
    queryFn: () => ApiService.apiDocumentsReferencesParserResultsRead(document.document_reference_id.toString()),
  });

  useEffect(() => {
    if (!parserResultsData?.url) {
      return;
    }
    fetch(parserResultsData.url)
      .then(r => r.json())
      .then((data: PageData[]) => setJsonData(data))
      .catch(err => Logger.error('Failed to fetch parser results JSON:', err));
  }, [parserResultsData]);

  const handleParsingJobComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: parserResultsQueryKey });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
  }, [queryClient, parserResultsQueryKey]);

  const completionContextRef = useRef({ companyId, currentMeasurementDate, referenceObjectType: 'ct', parserResultsQueryKey });

  const addParsingJobToProcessor = useCallback(
    (job: RetrieveDocumentGenerationJob) => {
      if (!parsingJobProcessorId) {
        return;
      }

      addJob({
        id: parsingJobProcessorId,
        details: {
          title: 'PDF Processing',
          description: 'AI Assisted PDF Processing',
        },
        pollingConfig: {
          pollingInterval: 3000,
          pollingArgs: [companyId, job.job_id],
          pollingFunction: CompanyService.apiCompanyDocumentGenerationJobsRead,
        },
        onResult: async (result: RetrieveDocumentGenerationJob) => {
          setRenderingJob(result);

          if (result.status !== RetrieveDocumentGenerationJob.status.COMPLETED) {
            return;
          }

          removeJob(parsingJobProcessorId);
          setFinalizingResults(true);
          const ctx = completionContextRef.current;
          try {
            if (ctx.companyId && ctx.currentMeasurementDate?.id) {
              await CompanyService.apiCompanyDocumentGenerationJobsDocumentExecutionSessionsList(
                ctx.companyId,
                result.job_id,
                undefined,
                true,
                ctx.currentMeasurementDate.id,
                ctx.referenceObjectType as 'ct' | 'fi' | 'fo' | 'cb' | 'ev' | 'ssv' | 'fio' | 'ma',
              );
            }
            queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: ctx.parserResultsQueryKey });
          } catch (error) {
            Logger.error('Error creating file references from global processor:', error);
          } finally {
            setFinalizingResults(false);
          }
        },
        onError: err => {
          Logger.error('Error polling parsing job:', err);
        },
      });
    },
    [addJob, removeJob, parsingJobProcessorId, companyId, queryClient]
  );

  const handleCitationPushed = useCallback(
    (payload: PDFCitationPayload) => {
      onCitationPushed?.(payload, document.document_reference_id);
    },
    [onCitationPushed, document.document_reference_id]
  );

  const handleSearchMatchSelect = useCallback((match: SearchMatch) => {
    setSearchHighlight({
      pageNumber: match.pageNumber,
      wordIds: match.matchedWordIds,
      rects: match.rects,
    });
  }, []);

  const handleSearchClose = useCallback(() => {
    setSearchOpen(false);
    setSearchHighlight(null);
  }, []);

  useEffect(() => {
    if (!jsonData) return;

    // When portaled into the popout window, keyboard events fire on that
    // window's object, not the opener's. Resolve via the DOM element so
    // Ctrl+F works in both docked and popped-out modes.
    const targetWindow = controlsRef.current?.ownerDocument?.defaultView ?? window;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    targetWindow.addEventListener('keydown', handleKeyDown);
    return () => targetWindow.removeEventListener('keydown', handleKeyDown);
  }, [jsonData, isPopped]);

  const handleConfirmAcceptAll = useCallback(async () => {
    setIsAccepting(true);
    try {
      await onAcceptAllCitations?.(document.document_reference_id);
    } finally {
      setIsAccepting(false);
      setAcceptAllDialogOpen(false);
    }
  }, [onAcceptAllCitations, document.document_reference_id]);

  const handlePageToggle = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(pageNumber)) {
        newSelected.delete(pageNumber);
      } else {
        newSelected.add(pageNumber);
      }
      return newSelected;
    });
  };

  const handleProcessDocument = useCallback(
    async (isTableExtraction: boolean = true) => {
      if (!document.file?.id || !companyId) {
        Logger.warn('Missing required data: file ID or company ID');
        return;
      }

      try {
        if (isTableExtraction) {
          setGenJobProcessing(true);
        } else {
          setRenderJobProcessing(true);
        }

        const parserOverrides = isTableExtraction
          ? [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: Array.from(selectedPages),
              pdf_parsing_mode: PDFGenerationJobParsingMode.TABULAR,
              convert_parsed_results_to_xlsx: true,
            },
          ]
          : [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: [],
              pdf_parsing_mode: PDFGenerationJobParsingMode.CITABLE,
              convert_parsed_results_to_xlsx: false,
            },
          ];

        const procType = {
          'Cap Table': ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
          Financials: ApiCreateDocumentGenerationJob.processing_type.FINANCIALS,
        };
        const processingType = procType[document.referenced_in?.[0]?.reference_object_type as keyof typeof procType];

        if (!processingType) {
          Logger.error('Invalid processing type:', document.referenced_in?.[0]?.reference_object_type);
          return;
        }

        const results = await apiCompanyDocumentGenerationJobsNewCreate(companyId, {
          reference_files: [document.file.id],
          processing_type: ApiCreateDocumentGenerationJob.processing_type.PARSING,
          desired_termination_state: GenerationJobState.PENDING_PROMPTING,
          parser_overrides: parserOverrides,
        });

        if (isTableExtraction) {
          setProcessingJob(results);
        } else {
          setRenderingJob(results);
          addParsingJobToProcessor(results);
          queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
        }
      } catch (error) {
        Logger.error(`Error ${isTableExtraction ? 'processing pages' : 'rendering document'}:`, error);
        if (isTableExtraction) {
          setGenJobProcessing(false);
        } else {
          setRenderJobProcessing(false);
        }
      }
    },
    [apiCompanyDocumentGenerationJobsNewCreate, companyId, document.file?.id, selectedPages, document.referenced_in, addParsingJobToProcessor, queryClient]
  );

  const handleProcessPages = useCallback(() => handleProcessDocument(true), [handleProcessDocument]);
  const handleRenderDocument = useCallback(() => handleProcessDocument(false), [handleProcessDocument]);

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const toggleSelectionMode = (enabled: boolean) => {
    setAllowPageSelection(enabled);
    if (!enabled) {
      setSelectedPages(new Set());
    }
  };

  const getReferenceObjectTypeCode = (displayType?: string): string => {
    const typeMap: Record<string, string> = {
      'Cap Table': 'ct',
      Financials: 'fi',
      'Fund Ownership': 'fo',
      'Custom Breakpoints': 'cb',
      'External Value': 'ev',
      'Specified Share Values': 'ssv',
      'Fund Investment Ownership Summary': 'fio',
      'Market Adjustment': 'ma',
    };

    if (displayType && displayType.length <= 3 && displayType === displayType.toLowerCase()) {
      return displayType;
    }

    return typeMap[displayType || ''] || 'ct';
  };

  const referenceObjectType = getReferenceObjectTypeCode(document.referenced_in?.[0]?.reference_object_type);

  // Keep completion context ref up to date for the global processor's onResult closure
  useEffect(() => {
    completionContextRef.current = { companyId, currentMeasurementDate, referenceObjectType, parserResultsQueryKey };
  }, [companyId, currentMeasurementDate, referenceObjectType, parserResultsQueryKey]);

  const jobAttempted = useRef(false);

  useEffect(() => {
    jobAttempted.current = false;
  }, [document.document_reference_id]);

  const isCapTable = referenceObjectType === 'ct';
  const hasExistingJob = !!file?.generation_job || hasGlobalParsingJob;
  const hasCompletedResults = !!file?.json_file_path;
  const jobInFlight = !!renderingJob || renderJobProcessing;
  const canResumeJob = !!(file?.generation_job_external_id && companyId);

  useEffect(() => {
    if (jobAttempted.current || jobInFlight) return;

    // Hydrate local state from a job already polling in the global processor
    if (hasGlobalParsingJob && canResumeJob) {
      jobAttempted.current = true;
      CompanyService.apiCompanyDocumentGenerationJobsRead(companyId, file.generation_job_external_id!)
        .then(job => setRenderingJob(job))
        .catch(err => Logger.error('Failed to hydrate rendering job from global processor:', err));
      return;
    }

    // Resume an existing job that hasn't produced results yet
    if (hasExistingJob && !hasCompletedResults && canResumeJob) {
      jobAttempted.current = true;
      CompanyService.apiCompanyDocumentGenerationJobsRead(companyId, file.generation_job_external_id!)
        .then(job => {
          if (job.status === RetrieveDocumentGenerationJob.status.FAILED) return;

          if (job.status === RetrieveDocumentGenerationJob.status.COMPLETED) {
            queryClient.invalidateQueries({ queryKey: [DOCUMENTS_BY_ID_QUERY_KEY] });
            return;
          }

          setRenderingJob(job);
          addParsingJobToProcessor(job);
        })
        .catch(err => Logger.error('Failed to resume existing generation job:', err));
      return;
    }

    // No job exists — auto-create for Cap Table documents
    if (isCapTable && !hasExistingJob) {
      jobAttempted.current = true;
      handleRenderDocument();
    }
  }, [isCapTable, hasGlobalParsingJob, hasExistingJob, hasCompletedResults, jobInFlight, canResumeJob, companyId, file?.generation_job_external_id, addParsingJobToProcessor, queryClient, handleRenderDocument]);

  const currentDocumentHasActiveCitation = useMemo(
    () => activeCitation?.document_reference === document.document_reference_id,
    [activeCitation, document]
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No document found: Current Document Preview size limit is 5MB</div>;
  }

  const canRetriggerCitable = !renderJobProcessing && !renderingJob;

  return (
    <Box>
      <Box ref={controlsRef}>
        {processingJob && currentMeasurementDate && (
          <PDFJobRibbon
            generationJob={processingJob}
            measurementDate={currentMeasurementDate}
            referenceObjectType={referenceObjectType}
          />
        )}

        {renderJobProcessing && !renderingJob && (
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
              Preparing document for citations...
            </Alert>
          </Box>
        )}

        {renderingJob && currentMeasurementDate && (
          <PDFJobRibbon
            generationJob={renderingJob}
            measurementDate={currentMeasurementDate}
            referenceObjectType={referenceObjectType}
            onJobComplete={handleParsingJobComplete}
            skipPolling
            onRetrigger={job => {
              setRenderingJob(job);
              addParsingJobToProcessor(job);
            }}
          />
        )}

        {finalizingResults && (
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ display: 'flex', alignItems: 'center' }}>
              Preparing citation data...
            </Alert>
          </Box>
        )}

        <PDFPageSelectionControls
          allowPageSelection={allowPageSelection}
          isPopped={isPopped}
          selectedPages={selectedPages}
          onToggleSelectionMode={toggleSelectionMode}
          onPageToggle={handlePageToggle}
          onClearSelection={handleClearSelection}
          onProcessPages={handleProcessPages}
          genJobProcessing={genJobProcessing}
          canRetriggerCitable={canRetriggerCitable}
          handleRenderDocument={handleRenderDocument}
          onAcceptAll={onAcceptAllCitations ? () => setAcceptAllDialogOpen(true) : undefined}
          acceptAllDisabled={!hasPendingCitationsForDocument?.(document.document_reference_id)}
          hasJsonData={!!jsonData}
          onSearchToggle={() => setSearchOpen(prev => !prev)}
          searchOpen={searchOpen}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          height: `calc(var(--app-draggable-drawer-height, 100vh) - ${controlsHeight + 36}px)`,
          overflow: 'hidden',
        }}>
        {searchOpen && jsonData && (
          <PDFDocumentSearch
            jsonData={jsonData}
            onMatchSelect={handleSearchMatchSelect}
            onClose={handleSearchClose}
          />
        )}

        <Box
          id="pdf-viewer-scroll-container"
          sx={{ height: '100%', overflow: 'auto' }}>
          <PDFViewer
            base64Content={data.content}
            src={data.blob_url}
            allowPageSelection={allowPageSelection}
            selectedPages={selectedPages}
            viewMode={allowPageSelection ? PDFViewerViewMode.THUMBNAIL : PDFViewerViewMode.DOCUMENT}
            onPageToggle={handlePageToggle}
            jsonData={jsonData ?? undefined}
            activeCitation={currentDocumentHasActiveCitation ? activeCitation : undefined}
            onCitationPushed={handleCitationPushed}
            searchHighlight={searchHighlight}
          />
        </Box>
      </Box>

      <Dialog open={acceptAllDialogOpen} onClose={() => !isAccepting && setAcceptAllDialogOpen(false)}>
        <DialogTitle>Accept All AI Citations</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to accept all AI citations for this document? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptAllDialogOpen(false)} color="primary" disabled={isAccepting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAcceptAll} variant="contained" disabled={isAccepting} sx={acceptAllConfirmButtonSx}>
            {isAccepting ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
            Yes, proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PDFViewerTab;
